import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import AccordionBox from "components/Layouts/AccordionBox";
import DropUploadBox from "components/Elements/DropUploadBox";
import { Apis } from "services/api";
import {
	getFeedbackDocuments,
	uploadFeedbackDocument,
	deleteFeedbackDocument,
} from "services/feedback/feedbackdetails";
import { useDispatch } from "react-redux";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import ColourConstants from "helpers/colourConstants";
import Row from "./Row";
import DeleteDialog from "pages/Models/ModelDetails/ModelDetail/DeleteDialog";
import { showError } from "../../../../redux/common/actions";

const useStyles = makeStyles()(() => ({
	logoContentParent: {
		width: "100%",
	},
	uploaderContainer: {
		marginTop: "2%",
	},
	tableHead: {
		backgroundColor: "#D2D2D9",
		border: "1px solid",
	},
	deleteIcon: {
		transform: "scale(0.7)",
		color: ColourConstants.deleteButton,
		"&:hover": {
			cursor: "pointer",
		},
	},
	documentError: {
		color: "#E21313",
		margin: "8px 0",
	},
}));

function Documents({ feedbackId, isReadOnly, customCaptions }) {
	const { t } = useTranslation();
	const { classes } = useStyles();
	const [filesUploading, setFilesUploading] = useState(false);
	const [listOfDocuments, setListOfDocuments] = useState([]);
	const [openDeleteDialog, setOpenDeleteDialog] = useState({
		isOpen: false,
		id: null,
	});
	const [isDeleting, setIsDeleting] = useState(false);
	const [documentError, setDocumentError] = useState("");
	const [uploadPercentCompleted, setUploadPercentCompleted] = useState(0);
	const cancelFileUpload = useRef(null);

	const dispatch = useDispatch();

	const fetchDocuments = async () => {
		const response = await getFeedbackDocuments(feedbackId);
		if (response.status) {
			setListOfDocuments(response.data);
		} else {
			dispatch(showError(t("message.couldNotLoadDocuments")));
		}
	};

	const onDocumentUpload = async (key) => {
		setFilesUploading(false);
		// The upload API returns the full S3 key including the "FeedbackDocuments/" prefix,
		// but the backend constructs the GET URL as FeedbackDocuments/{feedbackId}/{documentKey},
		// so we strip the redundant leading folder before saving.
		const documentKey = key.startsWith("FeedbackDocuments/")
			? key.slice("FeedbackDocuments/".length)
			: key;
		const response = await uploadFeedbackDocument({
			feedbackId,
			documentKey,
		});
		if (response.status) {
			setDocumentError("");
			await fetchDocuments();
		} else {
			dispatch(showError(t("message.couldNotUploadDocument")));
		}

		setUploadPercentCompleted(0);
	};

	const onOpenDeleteDialog = (id) => setOpenDeleteDialog({ isOpen: true, id });

	const onDeleteDialogClose = () =>
		setOpenDeleteDialog({ isOpen: false, id: null });

	const handleDelete = async () => {
		const { id } = openDeleteDialog;
		setIsDeleting(true);
		const response = await deleteFeedbackDocument(id);
		if (response.status) {
			setListOfDocuments([
				...listOfDocuments.filter((document) => document.id !== id),
			]);
		} else {
			dispatch(showError(t("message.couldNotDeleteDocument")));
		}

		setIsDeleting(false);
		onDeleteDialogClose();
	};

	useEffect(() => {
		fetchDocuments();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [feedbackId]);

	useEffect(() => {
		if (uploadPercentCompleted === 100) {
			setFilesUploading(false);
		}
	}, [uploadPercentCompleted]);

	return (
		<>
			<DeleteDialog
				open={openDeleteDialog.isOpen}
				closeHandler={onDeleteDialogClose}
				entityName="Feedback Document"
				handleDelete={handleDelete}
				isDeleting={isDeleting}
			/>
			<AccordionBox
				title={t("title.entityDocuments", {
					entity: customCaptions?.feedback ?? "Feedback",
					count: listOfDocuments.length,
				})}
			>
				<div className={classes.logoContentParent}>
					<Table>
						<TableHead className={classes.tableHead}>
							<TableRow>
								<TableCell style={{ width: "170px" }}>
									{t("label.filename")}
								</TableCell>
								<TableCell>{t("entity.user")}</TableCell>
								<TableCell>{t("label.date")}</TableCell>
								{isReadOnly ? null : <TableCell></TableCell>}
							</TableRow>
						</TableHead>
						<TableBody>
							{listOfDocuments.map((row) => (
								<Row
									key={row.id}
									row={row}
									classes={classes}
									onDeleteClick={() => onOpenDeleteDialog(row.id)}
									isReadOnly={isReadOnly}
								/>
							))}
						</TableBody>
					</Table>

					{isReadOnly ? null : (
						<div className={classes.uploaderContainer}>
							<DropUploadBox
								uploadReturn={onDocumentUpload}
								apiPath={`${Apis.Feedback}/${feedbackId}/uploadDocument`}
								filesUploading={filesUploading}
								setFilesUploading={setFilesUploading}
								isDocumentUploaded
								getError={setDocumentError}
								uploadPercentCompleted={uploadPercentCompleted}
								setUploadPercentCompleted={setUploadPercentCompleted}
								cancelFileUpload={cancelFileUpload}
								showProgress
							/>
							<p className={classes.documentError}>{documentError}</p>
						</div>
					)}
				</div>
			</AccordionBox>
		</>
	);
}

export default Documents;
